import { AfterViewChecked, AfterViewInit, Component, HostBinding, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import * as Plyr from 'plyr';
import { filter, Subscription } from 'rxjs';
import { Context } from 'src/app/context';
import { PlyrComponent } from 'src/app/lib/plyr/plyr.component';
import { ContentModel } from 'src/app/models/content.model';
import { ApiService } from 'src/app/services/api.service';
import { PlaybackService } from 'src/app/services/playback.service';
import { PlaybackEligibilityComponent } from './playback-eligibility/playback-eligibility.component';

@Component({
  selector: 'app-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss'],
})
export class AudioPlayerComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {

  @ViewChild('plyr') plyr: PlyrComponent;
  @ViewChild(PlaybackEligibilityComponent) eligibility: PlaybackEligibilityComponent;

  playlist: ContentModel[] = [];
  currentTrack: ContentModel | null = null;
  currentTrackIndex = 0;
  private viewInitialized = false;
  private loadingTrackIndex: number | null = null;
  private pendingTrackIndex: number | null = null;
  private fullPlayerRoute = false;
  private readonly subscriptions: Subscription[] = [];

  plyrOptions: Plyr.Options = {
    controls: ['play', 'progress', 'current-time', 'mute', 'volume'],
    autoplay: true,
    hideControls: false
  };

  @HostBinding('class.full-player-host')
  get fullPlayerHost() { return this.shouldShowPlayer && !this.isCompact; }

  @HostBinding('class.compact-player-host')
  get compactPlayerHost() { return this.shouldShowPlayer && this.isCompact; }

  get isCompact() {
    return !this.fullPlayerRoute && Context.brand?.appearance?.audioPlayerMode === 'mini';
  }

  get playerPosition() {
    return Context.brand?.appearance?.audioPlayerPosition === 'top' ? 'top' : 'bottom';
  }

  get shouldShowPlayer() {
    return this.playlist.length > 0 && (this.fullPlayerRoute || (!!this.currentTrack && this.isCompact));
  }

  constructor(
    private zone: NgZone,
    private api: ApiService,
    private playback: PlaybackService,
    private router: Router) {
    this.fullPlayerRoute = this.isPlayerRoute(this.router.url);
  }

  ngOnInit() {
    this.subscriptions.push(this.playback.playlist$.subscribe(playlist => {
      this.playlist = playlist;
      this.pendingTrackIndex = playlist.length ? 0 : null;
      this.tryStartPendingTrack();
    }));

    this.subscriptions.push(this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(event => {
      this.fullPlayerRoute = this.isPlayerRoute((event as NavigationEnd).urlAfterRedirects);
    }));
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    if (this.playlist.length)
      this.pendingTrackIndex = this.currentTrack ? this.currentTrackIndex : 0;
    this.tryStartPendingTrack();
  }

  ngAfterViewChecked() {
    // The player is created by an @if block after the parent view is ready.
    // Retry once the conditional Plyr and eligibility views are available.
    this.tryStartPendingTrack();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  plyrInit(player: Plyr) {
    this.addCustomControls(player);
    player.on('ended', () => this.playNextTrack());
    player.on('ready', () => this.addCustomControls(player));
    player.on('error', error => {
      console.error('Plyr error:', error);
      this.playNextTrack();
    });

    if (this.playlist.length) {
      this.pendingTrackIndex = this.currentTrack ? this.currentTrackIndex : 0;
      this.tryStartPendingTrack();
    }
  }

  addCustomControls(player: Plyr) {
    if (!player?.elements?.controls || player.elements.controls.querySelector('[data-plyr="prev"]'))
      return;

    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'plyr__control plyr__control--prev';
    prevButton.setAttribute('data-plyr', 'prev');
    prevButton.setAttribute('aria-label', 'Previous');
    prevButton.innerHTML = '<ion-icon name="play-skip-back" role="img" aria-label="Play Skip Back"></ion-icon>';
    player.elements.controls.insertBefore(prevButton, player.elements.controls.firstChild);
    prevButton.addEventListener('click', () => this.playPreviousTrack());

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'plyr__control plyr__control--next';
    nextButton.setAttribute('data-plyr', 'next');
    nextButton.setAttribute('aria-label', 'Next');
    nextButton.innerHTML = '<ion-icon name="play-skip-forward" role="img" aria-label="Play Skip Forward"></ion-icon>';
    player.elements.controls.appendChild(nextButton);
    nextButton.addEventListener('click', () => this.playNextTrack());
  }

  playNextTrack() {
    if (this.playlist.length)
      this.loadTrack((this.currentTrackIndex + 1) % this.playlist.length);
  }

  playPreviousTrack() {
    if (this.playlist.length)
      this.loadTrack((this.currentTrackIndex - 1 + this.playlist.length) % this.playlist.length);
  }

  async loadTrack(index: number) {
    if (!this.playlist.length || this.loadingTrackIndex === index || index < 0 || index >= this.playlist.length)
      return;

    if (!this.eligibility || !this.plyr?.player) {
      this.pendingTrackIndex = index;
      return;
    }

    this.pendingTrackIndex = null;
    this.loadingTrackIndex = index;
    try {
      if (!await this.eligibility.canPlay())
        return;

      const selectedTrack = this.playlist[index];
      const playback = await this.api.playContent(selectedTrack.id);

      this.zone.run(() => {
        if (!this.plyr?.player) return;
        this.currentTrack = selectedTrack;
        this.currentTrackIndex = index;
        this.plyr.player.source = {
          type: 'audio',
          sources: [{ src: playback.url, type: 'audio/wav' }]
        };
      });
    } catch (error) {
      console.error('Unable to start playback', error);
      await this.eligibility?.canPlay();
    } finally {
      this.loadingTrackIndex = null;
    }
  }

  selectTrack(index: number) {
    this.loadTrack(index);
  }

  openFullPlayer() {
    this.router.navigateByUrl('/tabs/player');
  }

  private isPlayerRoute(url: string) {
    const path = url.split('?')[0].replace(/\/$/, '');
    return path === '/player' || path === '/tabs/player';
  }

  private tryStartPendingTrack() {
    if (!this.viewInitialized || this.pendingTrackIndex === null || this.loadingTrackIndex !== null ||
      !this.eligibility || !this.plyr?.player)
      return;

    this.loadTrack(this.pendingTrackIndex);
  }
}
